import { float, max, mx_noise_float, smoothstep, vec3 } from 'three/tsl';
import type { SpeciesStyle, TreeStyle } from '../../engine/vegetation/VegetationStyle';

type N = any;
type RGB = [number, number, number];

/**
 * Boulder scatter shared by the hollows: rocks gather on slopes, at the foot of
 * cliffs and in outcrops, with a few lone boulders in open ground. They use the
 * GPU tree system with a boulder shape and their own placement mask.
 */
export function boulders(
  light: [RGB, RGB],
  dark: [RGB, RGB],
  opts: { scale?: number; glow?: () => N; glowShare?: number; lone?: number; minHeight?: number } = {},
): TreeStyle {
  const rock = (crown: [RGB, RGB], scale: number, glow?: () => N): SpeciesStyle => ({
    shape: 'boulder',
    bark: crown[0],
    crown,
    bendUp: 0,
    scale,
    sway: 0,
    glow,
    crownGlow: glow ? 1 : 0,
  });
  const s = opts.scale ?? 1;
  return {
    a: rock(light, 1.5 * s),
    b: rock(dark, 0.9 * s, opts.glow),
    aShare: [opts.glowShare !== undefined ? 1 - opts.glowShare : 0.55, 0.7],
    loneTrees: opts.lone ?? 0.015,
    minHeight: opts.minHeight ?? 3,
    range: 360,
    mask: ({ p, slope }) => {
      const outcrop = smoothstep(0.25, 0.5, mx_noise_float(p.mul(1 / 300).add(vec3(4.1, 0.7, 9.3))));
      // In clusters, not spread evenly: a field of them was one even carpet of stones.
      const cluster = smoothstep(-0.15, 0.25, mx_noise_float(p.mul(1 / 40).add(vec3(7.3, 2.2, 0.4))));
      // Not on the cliff faces themselves, where a boulder would hang in the air.
      return max(smoothstep(0.12, 0.3, slope), outcrop.mul(0.6))
        .mul(cluster.mul(0.8).add(0.2))
        .mul(float(1).sub(smoothstep(0.5, 0.7, slope)));
    },
  };
}
